import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Loader2,
  Save,
  X,
  Search,
  Edit2,
  Trash2,
  FileDown,
  Send,
} from "lucide-react";
import CONFIG from "config/config";

const API_BASE = (CONFIG.FAST_API || "http://localhost:8000") + "/api/v1/lab_cost";

interface LabTest {
  id: number;
  vendor_name: string;
  lab_code: string;
  test_name: string;
  method?: string;
  specimen?: string;
  turnaround_time?: string;
  cost?: number;
  storage_condition?: string;
  source_file?: string;
  page_number?: number;
}

interface ColumnWidths {
  select: number;
  vendor: number;
  labCode: number;
  testName: number;
  method: number;
  specimen: number;
  tat: number;
  cost: number;
  actions: number;
}

const DatabaseTab: React.FC = () => {
  const [items, setItems] = useState<LabTest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<LabTest>>({});
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [selectedVendor, setSelectedVendor] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [selectedPage, setSelectedPage] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [sendingHis, setSendingHis] = useState<boolean>(false);
  const [hisPayload, setHisPayload] = useState<any[] | null>(null);
  const [hisToken, setHisToken] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showTokenModal, setShowTokenModal] = useState<boolean>(false);
  const [confirmingHis, setConfirmingHis] = useState<boolean>(false);

  // Column Width State
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>({
    select: 50,
    vendor: 150,
    labCode: 120,
    testName: 200,
    method: 150,
    specimen: 120,
    tat: 120,
    cost: 100,
    actions: 100,
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/items`);
      setItems(response.data);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: LabTest) => {
    setEditingId(item.id);
    setEditForm({ ...item });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSave = async (id: number) => {
    try {
      await axios.put(`${API_BASE}/items/${id}`, editForm);
      setItems(
        items.map((item) => (item.id === id ? { ...item, ...editForm } : item)),
      );
      setEditingId(null);
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save changes.");
    }
  };

  const handleDeleteSelected = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedIds.length} items?`,
      )
    )
      return;

    try {
      await axios.post(`${API_BASE}/delete_items`, { item_ids: selectedIds });
      setItems(items.filter((item) => !selectedIds.includes(item.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete items.");
    }
  };

  const handleSendToHIS = () => {
    if (selectedIds.length === 0) return;
    setShowTokenModal(true);
  };

  const executeSendToHIS = async () => {
    if (!hisToken) {
      alert("Please enter HIS Token before sending.");
      return;
    }

    try {
      setShowTokenModal(false);
      setSendingHis(true);
      const response = await axios.post(`${API_BASE}/create_his_record`, {
        item_ids: selectedIds,
        token: hisToken,
      });
      console.log("HIS Payload received:", response.data);
      setHisPayload(response.data);
      setShowConfirmModal(true);
    } catch (err) {
      console.error("Send to HIS error:", err);
      alert("Failed to prepare items for HIS.");
    } finally {
      setSendingHis(false);
    }
  };

  const handleConfirmHIS = async () => {
    if (!hisPayload) return;
    if (!hisToken) {
      alert("Please enter HIS Token before confirming.");
      return;
    }

    try {
      setConfirmingHis(true);
      const response = await axios.post(
        `${API_BASE}/confirm_create_his_record`,
        {
          items: hisPayload,
          token: hisToken,
        },
      );
      console.log("HIS Confirmation Response:", response.data);
      alert(`Successfully sent ${hisPayload.length} items to HIS.`);
      setShowConfirmModal(false);
      setHisPayload(null);
      setHisToken("");
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      console.error("Confirm HIS error:", err);
      alert("Failed to send items to HIS.");
    } finally {
      setConfirmingHis(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof LabTest) => {
    setEditForm({ ...editForm, [field]: e.target.value });
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(filteredItems.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((itemId) => itemId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Resizing Logic
  const resizingRef = useRef<any>(null);

  const startResizing = (e: React.MouseEvent, columnKey: keyof ColumnWidths) => {
    e.preventDefault();
    resizingRef.current = {
      startX: e.clientX,
      startWidth: columnWidths[columnKey],
      columnKey: columnKey,
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!resizingRef.current) return;
    const { startX, startWidth, columnKey } = resizingRef.current;
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff);
    setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
  };

  const handleMouseUp = () => {
    resizingRef.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const uniqueVendors = Array.from(
    new Set(items.map((item) => item.vendor_name).filter(Boolean)),
  );
  const uniqueFiles = Array.from(
    new Set(items.map((item) => item.source_file).filter(Boolean)),
  );

  const availablePages = items
    .filter((item) => !selectedFile || item.source_file === selectedFile)
    .map((item) => item.page_number)
    .filter((page): page is number => page !== null && page !== undefined);
  const uniquePages = Array.from(new Set(availablePages)).sort((a, b) => a - b);

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      (item.vendor_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.lab_code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.test_name || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesVendor = selectedVendor
      ? item.vendor_name === selectedVendor
      : true;
    const matchesFile = selectedFile ? item.source_file === selectedFile : true;
    const matchesPage = selectedPage
      ? item.page_number === parseInt(selectedPage)
      : true;

    return matchesSearch && matchesVendor && matchesFile && matchesPage;
  });

  const handleExportCSV = () => {
    if (filteredItems.length === 0) return;

    const headers = [
      "Vendor",
      "Lab Code",
      "Test Name",
      "Method",
      "Specimen",
      "TAT",
      "Cost",
      "Storage",
      "Source File",
      "Page",
    ];
    const csvContent = [
      headers.join(","),
      ...filteredItems.map((item) =>
        [
          `"${item.vendor_name || ""}"`,
          `"${item.lab_code || ""}"`,
          `"${(item.test_name || "").replace(/"/g, '""')}"`,
          `"${(item.method || "").replace(/"/g, '""')}"`,
          `"${(item.specimen || "").replace(/"/g, '""')}"`,
          `"${item.turnaround_time || ""}"`,
          `"${item.cost || 0}"`,
          `"${(item.storage_condition || "").replace(/"/g, '""')}"`,
          `"${item.source_file || ""}"`,
          `"${item.page_number || ""}"`,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `lab_cost_export_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading)
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
      </div>
    );
  if (error) return <div className="text-center text-red-400 p-8">{error}</div>;

  const Resizer: React.FC<{ columnKey: keyof ColumnWidths }> = ({ columnKey }) => (
    <div
      onMouseDown={(e) => startResizing(e, columnKey)}
      className="absolute right-0 top-0 bottom-0 w-4 cursor-col-resize flex justify-center items-center group/resizer z-20 hover:bg-pink-100/50 transition-colors"
    >
      <div className="w-1 h-full bg-pink-100 group-hover/resizer:bg-pink-400 rounded-full transition-colors" />
    </div>
  );

  const totalWidth = Object.values(columnWidths).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6 animate-lab-cost-fade-in text-gray-700">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
        <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2">
          <span className="w-2 h-8 bg-pink-400 rounded-full inline-block"></span>
          Database Items
          <span className="text-sm font-normal text-gray-400 ml-2 bg-pink-50 px-2 py-1 rounded-full border border-pink-100">
            {filteredItems.length} rows
          </span>
        </h2>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {selectedIds.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="px-4 py-2 bg-red-400 hover:bg-red-500 text-white rounded-2xl text-sm font-bold transition-transform hover:-translate-y-0.5 shadow-md shadow-red-100 flex items-center"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedIds.length})
            </button>
          )}

          <button
            onClick={handleExportCSV}
            disabled={filteredItems.length === 0}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-2xl text-sm font-bold transition-transform hover:-translate-y-0.5 shadow-md shadow-green-100 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Export CSV
          </button>

          {selectedIds.length > 0 && (
            <button
              onClick={handleSendToHIS}
              disabled={selectedIds.length === 0 || sendingHis}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl text-sm font-bold transition-transform hover:-translate-y-0.5 shadow-md shadow-blue-100 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingHis ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send to HIS{" "}
              {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
            </button>
          )}

          <select
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
            className="p-2 pl-4 pr-8 text-sm border-2 border-pink-100 rounded-2xl bg-white focus:outline-none focus:border-pink-300 text-gray-600 cursor-pointer hover:border-pink-200 transition-colors"
          >
            <option value="">All Vendors</option>
            {uniqueVendors.map((vendor) => (
              <option key={vendor} value={vendor}>
                {vendor}
              </option>
            ))}
          </select>

          <select
            value={selectedFile}
            onChange={(e) => {
              setSelectedFile(e.target.value);
              setSelectedPage("");
            }}
            className="p-2 pl-4 pr-8 text-sm border-2 border-pink-100 rounded-2xl bg-white focus:outline-none focus:border-pink-300 text-gray-600 cursor-pointer hover:border-pink-200 transition-colors"
          >
            <option value="">All Files</option>
            {uniqueFiles.map((file) => (
              <option key={file} value={file}>
                {file}
              </option>
            ))}
          </select>

          <select
            value={selectedPage}
            onChange={(e) => setSelectedPage(e.target.value)}
            disabled={uniquePages.length === 0}
            className="p-2 pl-4 pr-8 text-sm border-2 border-pink-100 rounded-2xl bg-white focus:outline-none focus:border-pink-300 text-gray-600 cursor-pointer hover:border-pink-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">All Pages</option>
            {uniquePages.map((page) => (
              <option key={page} value={page.toString()}>
                Page {page}
              </option>
            ))}
          </select>

          <div className="relative flex-grow md:flex-grow-0 group">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-4 h-4 text-pink-300 group-focus-within:text-pink-400 transition-colors" />
            </div>
            <input
              type="text"
              className="block w-full md:w-64 p-2 pl-10 text-sm text-gray-700 border-2 border-pink-100 rounded-2xl bg-white focus:ring-4 focus:ring-pink-50 focus:border-pink-300 outline-none transition-all placeholder:text-gray-300"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto shadow-xl shadow-pink-100/50 rounded-[2rem] border border-pink-50 bg-white">
        <table
          className="text-sm text-left text-gray-600"
          style={{
            tableLayout: "fixed",
            width: `${totalWidth}px`,
            minWidth: "100%",
          }}
        >
          <thead className="text-xs text-pink-600 uppercase bg-pink-50/80">
            <tr>
              <th
                scope="col"
                className="p-4 relative"
                style={{ width: columnWidths.select }}
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-pink-500 bg-white border-pink-200 rounded focus:ring-pink-400 focus:ring-2"
                    onChange={handleSelectAll}
                    checked={
                      filteredItems.length > 0 &&
                      selectedIds.length === filteredItems.length
                    }
                  />
                  <label className="sr-only">checkbox</label>
                </div>
                <Resizer columnKey="select" />
              </th>
              <th
                scope="col"
                className="px-4 py-4 font-bold relative"
                style={{ width: columnWidths.vendor }}
              >
                Vendor <Resizer columnKey="vendor" />
              </th>
              <th
                scope="col"
                className="px-4 py-4 font-bold relative"
                style={{ width: columnWidths.labCode }}
              >
                Lab Code <Resizer columnKey="labCode" />
              </th>
              <th
                scope="col"
                className="px-4 py-4 font-bold relative"
                style={{ width: columnWidths.testName }}
              >
                Test Name <Resizer columnKey="testName" />
              </th>
              <th
                scope="col"
                className="px-4 py-4 font-bold relative"
                style={{ width: columnWidths.method }}
              >
                Method <Resizer columnKey="method" />
              </th>
              <th
                scope="col"
                className="px-4 py-4 font-bold relative"
                style={{ width: columnWidths.specimen }}
              >
                Specimen <Resizer columnKey="specimen" />
              </th>
              <th
                scope="col"
                className="px-4 py-4 font-bold relative"
                style={{ width: columnWidths.tat }}
              >
                TAT <Resizer columnKey="tat" />
              </th>
              <th
                scope="col"
                className="px-4 py-4 font-bold relative"
                style={{ width: columnWidths.cost }}
              >
                Cost <Resizer columnKey="cost" />
              </th>
              <th
                scope="col"
                className="px-4 py-4 font-bold text-center relative"
                style={{ width: columnWidths.actions }}
              >
                Actions <Resizer columnKey="actions" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => (
              <tr
                key={item.id}
                className={`border-b border-pink-50 transition-colors hover:bg-pink-50/30 ${selectedIds.includes(item.id) ? "bg-pink-50/50" : ""}`}
              >
                <td className="p-4 overflow-hidden text-ellipsis whitespace-nowrap">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-pink-500 bg-white border-pink-200 rounded focus:ring-pink-400 focus:ring-2"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleSelectRow(item.id)}
                    />
                    <label className="sr-only">checkbox</label>
                  </div>
                </td>
                {editingId === item.id ? (
                  <>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editForm.vendor_name || ""}
                        onChange={(e) => handleChange(e, "vendor_name")}
                        className="w-full p-2 border-2 border-pink-100 rounded-xl focus:border-pink-300 outline-none"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editForm.lab_code || ""}
                        onChange={(e) => handleChange(e, "lab_code")}
                        className="w-full p-2 border-2 border-pink-100 rounded-xl focus:border-pink-300 outline-none"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editForm.test_name || ""}
                        onChange={(e) => handleChange(e, "test_name")}
                        className="w-full p-2 border-2 border-pink-100 rounded-xl focus:border-pink-300 outline-none"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editForm.method || ""}
                        onChange={(e) => handleChange(e, "method")}
                        className="w-full p-2 border-2 border-pink-100 rounded-xl focus:border-pink-300 outline-none"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editForm.specimen || ""}
                        onChange={(e) => handleChange(e, "specimen")}
                        className="w-full p-2 border-2 border-pink-100 rounded-xl focus:border-pink-300 outline-none"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={editForm.turnaround_time || ""}
                        onChange={(e) => handleChange(e, "turnaround_time")}
                        className="w-full p-2 border-2 border-pink-100 rounded-xl focus:border-pink-300 outline-none"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.cost || 0}
                        onChange={(e) => handleChange(e, "cost")}
                        className="w-full p-2 border-2 border-pink-100 rounded-xl focus:border-pink-300 outline-none"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleSave(item.id)}
                          className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 font-semibold text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.vendor_name}
                    </td>
                    <td className="px-4 py-3 overflow-hidden text-ellipsis whitespace-nowrap">
                      <span className="font-mono text-xs bg-gray-50/50 rounded-lg px-2 py-1">
                        {item.lab_code}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3 overflow-hidden text-ellipsis whitespace-nowrap"
                      title={item.test_name}
                    >
                      {item.test_name}
                    </td>
                    <td className="px-4 py-3 text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.method || "-"}
                    </td>
                    <td
                      className="px-4 py-3 text-xs overflow-hidden text-ellipsis whitespace-nowrap"
                      title={item.specimen}
                    >
                      {item.specimen || "-"}
                    </td>
                    <td className="px-4 py-3 text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.turnaround_time || "-"}
                    </td>
                    <td className="px-4 py-3 font-bold text-green-500 overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.cost !== undefined && item.cost !== null
                        ? `${Number(item.cost).toFixed(2)}`
                        : "N/A"}
                    </td>
                    <td className="px-4 py-3 text-center overflow-hidden whitespace-nowrap">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-all hover:scale-110"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {filteredItems.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-12 text-center text-gray-400"
                >
                  <div className="flex flex-col items-center">
                    <Search className="w-8 h-8 mb-2 text-pink-200" />
                    <p>No items found matching your search.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden border border-pink-100 animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-pink-50 flex justify-between items-center bg-pink-50/30">
              <h3 className="text-xl font-bold text-gray-700 flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-500" />
                Review HIS Payload
                <span className="text-sm font-normal text-gray-400 ml-2 bg-white px-2 py-1 rounded-full border border-pink-100">
                  {hisPayload?.length || 0} items
                </span>
              </h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="p-2 hover:bg-pink-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="flex-grow overflow-auto p-6">
              <div className="space-y-4">
                {hisPayload?.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-2xl bg-gray-50 border border-gray-100 hover:border-blue-200 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-blue-600">{item.name}</div>
                      <div className="text-xs font-mono bg-blue-50 text-blue-500 px-2 py-1 rounded-lg">
                        {item.id ? (
                          `HIS ID: ${item.id}`
                        ) : (
                          <>
                            <span className="text-red-500 font-bold">**</span>{" "}
                            NEW Lab Test
                          </>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <div className="text-gray-400 mb-1">Code</div>
                        <div className="font-semibold">{item.code || "-"}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 mb-1">acronym</div>
                        <div className="font-semibold">
                          {item.acronym || "-"}
                        </div>
                      </div>

                      <div>
                        <div className="text-gray-400 mb-1">Specimen</div>
                        <div className="font-semibold">
                          {item.specimen_name || "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 mb-1">Speciality</div>
                        <div className="font-semibold">
                          {item.lab_speciality_name ||
                            item.lab_speciality ||
                            "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 mb-1">Lab Type</div>
                        <div className="font-semibold">
                          {item.lab_type_label || item.lab_type || "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 mb-1">Lab Division</div>
                        <div className="font-semibold">
                          {item.lab_division_name || ""}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 mb-1">Lab Unit</div>
                        <div className="font-semibold">
                          {item.lab_unit_name || ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="pt-5 text-green-600">
                        {item.costs?.[0]?.vendor_name || ""}
                      </div>
                      <div className="pt-5 font-bold text-green-600">
                        {item.costs?.[0]?.standard_cost?.toFixed(2) || "0.00"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-t border-pink-50 bg-gray-50/50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                }}
                className="px-6 py-2.5 bg-white border-2 border-gray-100 hover:border-gray-200 text-gray-500 rounded-2xl font-bold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmHIS}
                disabled={confirmingHis}
                className="px-8 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl font-bold transition-all shadow-lg shadow-blue-100 flex items-center gap-2 disabled:opacity-50"
              >
                {confirmingHis ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Confirm & Send to HIS
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTokenModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 border-blue-100 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <Save className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-700">Enter HIS Token</h3>
              <p className="text-sm text-gray-400 mt-1">Please provide your authorization token to proceed.</p>
            </div>

            <div className="relative group mb-8">
              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                <Save className="w-5 h-5 text-blue-300 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="password"
                autoFocus
                placeholder="Paste your token here..."
                value={hisToken}
                onChange={(e) => setHisToken(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && executeSendToHIS()}
                className="block w-full p-3.5 pl-12 text-sm text-gray-700 border-2 border-blue-100 rounded-2xl bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-300 outline-none transition-all shadow-inner"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowTokenModal(false)}
                className="flex-1 px-6 py-3 bg-white border-2 border-gray-100 hover:border-gray-200 text-gray-500 rounded-2xl font-bold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={executeSendToHIS}
                className="flex-1 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl font-bold transition-all shadow-lg shadow-blue-100"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseTab;
